import React, { useState, useMemo, useRef, useLayoutEffect, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';

import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

import './NavbarD5.scss';
import LogoContainer from 'containers/LogoContainer';
import { getCustomDesignSettings, isCustomDesign } from 'constants/customDesign';
import { getStoreDesign } from 'constants/storeDesigns';
import { formatCurrencyARS } from 'helpers/currency';
import { getDesignMenuProfile } from './designMenuProfiles';

const NavbarD5 = ({
  cartSize,
  cartTotalFormatted,
  categories = [],
  subCategories = [],
  user,
  logout,
  openLoginModal,
  products = [],
  setSelectedProduct,
  selectedProduct,
  onSearchProductChange,
  searchProduct = '',
  onSubmit,
  client,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [dropdownSide, setDropdownSide] = useState('left');

  const menuTimer = useRef(null);
  const menuRef = useRef(null);
  const navRef = useRef(null);

  // Evita scroll de fondo cuando el drawer está abierto
  useLayoutEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Cierra dropdown al clickear fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Publica la altura real del navbar como variable CSS.
  // Los breadcrumbs y secciones la usan para no quedar pegados ni debajo del header fijo.
  useLayoutEffect(() => {
    const previousBodyPaddingTop = document.body.style.getPropertyValue('padding-top');
    const previousBodyPaddingPriority = document.body.style.getPropertyPriority('padding-top');

    const updateNavHeight = () => {
      const header = navRef.current?.closest('.header-store-design');
      const height = header?.offsetHeight || navRef.current?.offsetHeight || 72;
      document.documentElement.style.setProperty('--d5-nav-h', `${height}px`);
      document.documentElement.style.setProperty('--header-total', `${height}px`);
      document.body.style.setProperty('padding-top', `${height}px`, 'important');
    };

    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && navRef.current) {
      const header = navRef.current.closest('.header-store-design');
      resizeObserver = new ResizeObserver(updateNavHeight);
      resizeObserver.observe(header || navRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateNavHeight);
      if (resizeObserver) resizeObserver.disconnect();
      if (previousBodyPaddingTop) {
        document.body.style.setProperty('padding-top', previousBodyPaddingTop, previousBodyPaddingPriority);
      } else {
        document.body.style.removeProperty('padding-top');
      }
    };
  }, [mobileSearchOpen, categories.length]);

  const openDeskMenu = () => {
    if (menuTimer.current) clearTimeout(menuTimer.current);
    const rect = menuRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    setDropdownSide(rect && rect.left > viewportWidth / 2 ? 'right' : 'left');
    setIsMenuOpen(true);
  };
  const closeDeskMenu = (delay = 120) => {
    if (menuTimer.current) clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => setIsMenuOpen(false), delay);
  };

  const subsByCatId = useMemo(() => {
    const m = {};
    (subCategories ?? []).forEach(sc => {
      if (!m[sc.category_id]) m[sc.category_id] = [];
      m[sc.category_id].push(sc);
    });
    return m;
  }, [subCategories]);

  const handleSearchSubmit = () => {
    if (typeof onSubmit === 'function') onSubmit();
  };

  const customSettings = isCustomDesign(client) ? getCustomDesignSettings(client) : null;
  const storeDesign = getStoreDesign(client?.design);
  const menuStyle = customSettings?.menuStyle || storeDesign?.nav || 'dropdown';
  const designSlug = storeDesign?.slug || (customSettings ? 'custom' : 'legacy');
  const menuProfile = getDesignMenuProfile(storeDesign?.slug);
  const showCategoryStrip = !!menuProfile.showStrip;
  const visibleCategories = categories.slice(0, menuProfile.maxCategories);

  const renderSearchOption = (opt) => {
    if (typeof opt === 'string') {
      return (
        <div className="search-item">
          <div className="search-item__img" />
          <div className="search-item__info">
            <div className="search-item__title">{opt}</div>
          </div>
        </div>
      );
    }
    const price = opt?.price ?? 0;
    return (
      <div className="search-item" data-testid={`search-item-${opt?.id || 's'}`}>
        <div className="search-item__img">
          <img src={opt?.image1 || '/images/placeholder.png'} alt={opt?.name || ''} loading="lazy" />
        </div>
        <div className="search-item__info">
          <div className="search-item__title" title={opt?.name || ''}>
            {opt?.name || ''}
          </div>
          <div className="search-item__price">
            {opt?.sale ? (
              <>
                <span className="search-item__price--sale">
                  {formatCurrencyARS(opt.sale)}
                </span>
                <span className="search-item__price--old">
                  {formatCurrencyARS(price)}
                </span>
              </>
            ) : (
              <span>{formatCurrencyARS(price)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="nabar-d5">
      {/* ===== NAVBAR (STICKY) ===== */}
      <nav
        ref={navRef}
        className={`d5-navbar d5-navbar--${menuStyle} d5-navbar--design-${designSlug} d5-navbar--profile-${menuProfile.layout}`}
      >
        <div className="d5-inner">
          {/* MENÚ (desktop): dropdown */}
          <div
            className={`d5-menuPill d5-menuPill--dropdown-${dropdownSide}`}
            onMouseEnter={openDeskMenu}
            onMouseLeave={() => closeDeskMenu()}
            ref={menuRef}
          >
            <button
              type="button"
              className="d5-menuTrigger d5-icon--bare"
              aria-haspopup="true"
              aria-expanded={isMenuOpen}
              aria-controls="d5-menu-panel"
              onClick={(e) => e.preventDefault()}
            >
              <MenuIcon />
            </button>

            <div
              id="d5-menu-panel"
              className={`d5-menuDropdown d5-menuDropdown--${menuStyle} d5-menuDropdown--profile-${menuProfile.layout} ${isMenuOpen ? 'open' : ''}`}
              role="menu"
            >
              <ul className="d5-menuList">
                {categories.map(cat => (
                  <li key={cat.id} className="d5-menuItem">
                    <NavLink
                      to={`/productos/${cat.code}`}
                      className="d5-catLink"
                      onClick={() => setIsMenuOpen(false)}
                      role="menuitem"
                    >
                      {cat.name}
                      {subsByCatId[cat.id]?.length ? <span className="chev" aria-hidden="true">⌄</span> : null}
                    </NavLink>

                    {subsByCatId[cat.id]?.length ? (
                      <ul className="d5-subList">
                        {subsByCatId[cat.id].map(sub => (
                          <li key={sub.id}>
                            <NavLink
                              to={`/productos/${cat.code}/${sub.code}`}
                              onClick={() => setIsMenuOpen(false)}
                              role="menuitem"
                            >
                              {sub.name}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Logo (desktop) */}
          <Link to="/" className="d5-logo" aria-label="Inicio">
            <LogoContainer />
          </Link>

          {/* Buscador DESKTOP — bonito */}
          <div className="d5-searchShell">
            <div className="d5-searchBar d5-searchBar--pretty">
              <div className="d5-searchInput">
                <Autocomplete
                  key="auto-complete-search"
                  options={products}
                  freeSolo
                  filterOptions={(options) => options}
                  onChange={(event, product) => setSelectedProduct(event, product)}
                  value={selectedProduct ?? null}
                  onInputChange={onSearchProductChange}
                  inputValue={searchProduct}
                  getOptionSelected={(option, product) => option?.id === product?.id}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name ?? ''))}
                  renderOption={renderSearchOption}
                  noOptionsText="Sin resultados"
                  openOnFocus
                  blurOnSelect
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={menuProfile.searchPlaceholder}
                      variant="outlined"
                      size="small"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                      InputProps={{
                        ...params.InputProps,
                        notched: false,
                      }}
                    />
                  )}
                />
              </div>

              <button
                type="button"
                className="d5-searchBtn d5-searchBtn--integrated"
                aria-label="Buscar"
                onClick={handleSearchSubmit}
                disabled={!searchProduct?.length}
              >
                <SearchIcon />
              </button>
            </div>
          </div>

          {/* Acciones derecha (desktop) */}
          <div className="d5-right">
            <Link to="/carrito" className="d5-iconLink d5-icon--bare d5-cartBtn" aria-label="Carrito">
              <ShoppingCartOutlinedIcon />
              {!!Number(cartSize) && <span className="d5-cartBadge">{cartSize}</span>}
              {!!Number(cartSize) && <span className="cart-total-pill">{cartTotalFormatted}</span>}
            </Link>

            {!user ? (
              <button className="d5-iconLink d5-icon--bare" onClick={openLoginModal} aria-label="Iniciar sesión">
                <PersonOutlineIcon />
              </button>
            ) : (
              <button className="d5-iconLink d5-icon--bare" onClick={logout} aria-label="Cerrar sesión">
                <ExitToAppIcon />
              </button>
            )}
          </div>

          {/* Header compacto (mobile) */}
          <div className="d5-compact">
            <button className="iconbtn iconbtn--bare" aria-label="Abrir menú" onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </button>

            <Link to="/" className="d5-logoMobile" aria-label="Inicio">
              <LogoContainer />
            </Link>

            <div className="d5-compactActions">
              <button className="iconbtn iconbtn--bare" aria-label="Buscar" onClick={() => setMobileSearchOpen(s => !s)}>
                <SearchIcon />
              </button>

              <Link to="/carrito" className="iconbtn iconbtn--bare d5-cartBtn" aria-label="Carrito">
                <ShoppingCartOutlinedIcon />
                {!!Number(cartSize) && <span className="d5-cartBadge">{cartSize}</span>}
                {!!Number(cartSize) && <span className="cart-total-pill">{cartTotalFormatted}</span>}
              </Link>

              {!user ? (
                <button className="iconbtn iconbtn--bare" aria-label="Iniciar sesión" onClick={openLoginModal}>
                  <PersonOutlineIcon />
                </button>
              ) : (
                <button className="iconbtn iconbtn--bare" aria-label="Cerrar sesión" onClick={logout}>
                  <ExitToAppIcon />
                </button>
              )}
            </div>
          </div>
        </div>

        {showCategoryStrip && (
          <div
            className={`d5-categoryStrip d5-categoryStrip--${menuStyle} d5-categoryStrip--profile-${menuProfile.layout}`}
            aria-label={menuProfile.stripAriaLabel}
          >
            <div className="d5-categoryStrip__inner">
              {visibleCategories.map((cat, index) => (
                <NavLink key={cat.id} to={`/productos/${cat.code}`} className="d5-stripLink">
                  {menuProfile.showIndex && <span className="d5-stripLink__index">{String(index + 1).padStart(2, '0')}</span>}
                  <span className="d5-stripLink__text">{cat.name}</span>
                </NavLink>
              ))}
              <NavLink to="/productos" className="d5-stripLink d5-stripLink--all">
                {menuProfile.showIndex && <span className="d5-stripLink__index">+</span>}
                <span className="d5-stripLink__text">{menuProfile.allLabel}</span>
              </NavLink>
            </div>
          </div>
        )}

        {/* Barra de búsqueda MOBILE (plegable) */}
        {mobileSearchOpen && (
          <div className="d5-mobileSearch">
            <div className="d5-searchBar d5-searchBar--pretty">
              <div className="d5-searchInput">
                <Autocomplete
                  key="auto-complete-search-mobile"
                  options={products}
                  freeSolo
                  filterOptions={(options) => options}
                  onChange={(event, product) => setSelectedProduct(event, product)}
                  value={selectedProduct ?? null}
                  onInputChange={onSearchProductChange}
                  inputValue={searchProduct}
                  getOptionSelected={(option, product) => option?.id === product?.id}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name ?? ''))}
                  renderOption={renderSearchOption}
                  noOptionsText="Sin resultados"
                  openOnFocus
                  blurOnSelect
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={menuProfile.searchPlaceholder}
                      variant="outlined"
                      size="small"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                      InputProps={{
                        ...params.InputProps,
                        notched: false,
                      }}
                    />
                  )}
                />
              </div>

              <button
                type="button"
                className="d5-searchBtn d5-searchBtn--round"
                aria-label="Buscar"
                onClick={handleSearchSubmit}
                disabled={!searchProduct?.length}
              >
                <SearchIcon />
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Drawer móvil */}
      {drawerOpen && <div className="d5-overlay" onClick={() => setDrawerOpen(false)} />}
      <aside className={`d5-drawer${drawerOpen ? ' open' : ''}`} role="dialog" aria-modal="true">
        <button className="d5-drawerClose" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menú">
          <CloseIcon />
        </button>
        <ul className="d5-drawerList">
          {categories.map((cat) => (
            <li key={cat.id}>
              <NavLink to={`/productos/${cat.code}`} onClick={() => setDrawerOpen(false)}>
                {cat.name}
              </NavLink>
              {subsByCatId[cat.id]?.length ? (
                <ul className="d5-drawerSubs">
                  {subsByCatId[cat.id].map((sub) => (
                    <li key={sub.id}>
                      <NavLink to={`/productos/${cat.code}/${sub.code}`} onClick={() => setDrawerOpen(false)}>
                        {sub.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
};

export default NavbarD5;
